import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  InputAdornment,
} from '@mui/material';
import { fetchMyProfile, upsertMyProfile, isSlugAvailable, normalizeSlug, isValidSlug } from '../../lib/profile';
import { UserProfile } from '../../types';

export default function ProfilePage() {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    fetchMyProfile().then(({ data, error }) => {
      if (error) setSaveError(error);
      if (data) {
        setProfile(data);
        setSlug(data.slug);
        setDisplayName(data.display_name ?? '');
        setBio(data.bio ?? '');
      }
      setLoading(false);
    });
  }, []);

  // Debounced slug availability check
  useEffect(() => {
    if (!slug || slug === profile?.slug) {
      setSlugStatus('idle');
      return;
    }
    if (!isValidSlug(slug)) {
      setSlugStatus('invalid');
      return;
    }
    setSlugStatus('checking');
    const timer = setTimeout(() => {
      isSlugAvailable(slug).then(({ available }) => {
        setSlugStatus(available ? 'available' : 'taken');
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [slug, profile?.slug]);

  async function handleSave() {
    setSaveError(null);
    setSaveMessage(null);
    if (!isValidSlug(slug)) {
      setSaveError('Slug must be lowercase letters, digits, and hyphens; 2–40 chars.');
      return;
    }
    if (slugStatus === 'taken') {
      setSaveError('That slug is already in use.');
      return;
    }
    setSaving(true);
    const { data, error } = await upsertMyProfile({
      slug,
      display_name: displayName.trim() || null,
      bio: bio.trim() || null,
    });
    setSaving(false);
    if (error) {
      setSaveError(error);
      return;
    }
    if (data) {
      setProfile(data);
      setSaveMessage('Saved.');
    }
  }

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  const slugHelper =
    slugStatus === 'invalid'
      ? 'Invalid — lowercase letters, digits, hyphens (2–40 chars).'
      : slugStatus === 'checking'
      ? 'Checking availability…'
      : slugStatus === 'available'
      ? '✓ Available'
      : slugStatus === 'taken'
      ? '✗ Already in use'
      : profile
      ? `Public profile: hiyouji.com/${slug}`
      : 'Public profile URL — pick something short and memorable.';

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h4" sx={{ mb: 3, color: 'primary.main', fontWeight: 600 }}>Profile</Typography>

      <Stack spacing={3}>
        <TextField
          label="Slug"
          value={slug}
          onChange={(e) => setSlug(normalizeSlug(e.target.value))}
          fullWidth
          helperText={slugHelper}
          error={slugStatus === 'invalid' || slugStatus === 'taken'}
          slotProps={{
            input: {
              startAdornment: <InputAdornment position="start">hiyouji.com/</InputAdornment>,
            },
          }}
        />

        <TextField
          label="Display name"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          fullWidth
          helperText="Shown on your public profile and trip pages. Optional."
        />

        <TextField
          label="Bio"
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          fullWidth
          multiline
          minRows={3}
          helperText="A short intro shown on your public profile. Optional."
        />

        {saveMessage ? <Alert severity="success">{saveMessage}</Alert> : null}
        {saveError ? <Alert severity="error">{saveError}</Alert> : null}

        <Box>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || slugStatus === 'checking' || slugStatus === 'taken' || slugStatus === 'invalid' || !slug}
          >
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Save'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
